import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import {
  buttonClasses,
  usePlanYears,
  type CostToolEstimate,
  type CostToolEstimateDrug,
  type CostToolPlanOption,
  type Drug,
} from "@dearbill/shared";
import { useApi } from "../auth/useApi";
import { useOrg } from "../shell/OrgContext";
import { StateSection } from "../components/costTool/StateSection";
import { PlanSection } from "../components/costTool/PlanSection";
import { MedicationsSection, MAX_DRUGS } from "../components/costTool/MedicationsSection";
import { ResultScreen } from "../components/costTool/ResultScreen";
import { HandoffSection } from "../components/costTool/HandoffSection";
import { HelpWalkthrough } from "../components/costTool/HelpWalkthrough";
import { HistoryPanel } from "../components/costTool/HistoryPanel";
import {
  useCostToolMemory,
  type CostToolHistoryEntry,
} from "../components/costTool/useCostToolMemory";
import calculatorIcon from "../assets/icon-calculator.svg";
import type { StepStatus } from "../components/costTool/SectionHeading";

/** Debounce before the estimate POST, so drugs added in quick succession coalesce into one call. */
const ESTIMATE_DEBOUNCE_MS = 300;

/**
 * The Member cost share calculator (`/orgs/:orgId/cost-tool`), a two-screen
 * flow (Figma dR8cJ6hRSW5eERToo64boe, nodes 2340:138 and 2340:232).
 *
 * The form screen (the index route) asks three numbered questions: the
 * patient's state, their plan, and their medications. Per-medication coverage
 * and cost chips keep updating live as the form changes; only the headline
 * answer lives on the result screen. The "See the member's cost share" button
 * navigates to `result`, and the referral handoff stays reachable on both
 * screens, because even "can't find the plan" refers fine.
 *
 * The result screen (`cost-tool/result`) carries the verdict banner, the two
 * comparison tiles, the always-open medication-detail table and the actions
 * row. A direct hit with nothing to show (no plan or no medications, as after
 * a full page load) redirects to the form.
 *
 * This component holds all calculator state above a descendant `<Routes>`, so
 * both screens share it and a back-and-forth between them never loses inputs.
 *
 * Compliance: copy never says M3P saves money, because it changes the timing
 * of payments, not the yearly total. The headline tiles and medication chips
 * show rounded estimates prefixed "est.", never a quote, and the result
 * screen's always-visible fine print names the 30-day-fill and retail-pricing
 * assumptions.
 *
 * Estimate lifecycle: whenever a plan and at least one drug are set, the
 * screen POSTs `/api/partner/orgs/{orgId}/cost-tool/estimate` after
 * `ESTIMATE_DEBOUNCE_MS`. A sequence number discards stale responses. A refresh
 * keeps the previous numbers on screen, and a failure shows a Retry alert.
 * Clearing the plan or the last drug clears the result. Each estimate is stored
 * with the input key it answered (`estimateKey`). When the inputs change and
 * the refresh fails, the old numbers answer a different question, so
 * ResultScreen gets `stale` and hides them. A failure with unchanged inputs
 * keeps the old numbers visible beside the alert.
 *
 * Per-fill quantity overrides (`quantities`): the engine assumes a per-fill
 * quantity, and each drug row lets the partner edit it. Only partner-edited
 * entries are held and sent. An edit re-runs the estimate through the same
 * debounce, and removing a drug drops its override. History entries never
 * store overrides, so a restore re-derives them.
 *
 * Repeat-visit memory (`useCostToolMemory`, org-scoped localStorage): the last
 * US state is preselected, recent plans and drugs show as chips, and every
 * selection is remembered. Choosing a plan moves focus to the drug input, and
 * each added drug returns focus there. Nothing about the patient is stored.
 *
 * Recent checks (`HistoryPanel`): each settled estimate with known coverage
 * records its inputs and headline numbers. Only a settled estimate records,
 * and re-checking the same plan, plan year and drug set replaces its entry.
 * Restoring an entry fills the form and re-runs the estimate against current
 * data.
 *
 * Plan year: while `GET /api/plans/years` offers a next year, the plan step
 * carries the shared `PlanYearToggle`, defaulting to next year, and the plan
 * search, the estimate POST and the refer-email context carry `planYear`.
 * Without a next year the effective `planYear` is null and nothing carries it
 * (the current year). A year switch clears the selected plan, because the
 * other year's list may not hold it; the drugs stay. Every remembered plan and
 * check stores the year it ran under, recent-plan chips show only the effective
 * year's plans, and a restore re-runs under the entry's year.
 */
export function CostToolTab(): ReactNode {
  const api = useApi();
  const { orgId } = useOrg();
  const navigate = useNavigate();
  const location = useLocation();
  // Absolute sub-route targets. Relative paths from a splat route (`cost-tool/*`
  // in App.tsx) resolve against route levels, not URL segments, which is the
  // same trap `OrgCatchAllRedirect` documents; absolute targets sidestep it.
  const formPath = `/orgs/${orgId}/cost-tool`;
  const resultPath = `${formPath}/result`;
  const {
    memory,
    rememberState,
    rememberPlan,
    rememberDrug,
    rememberCheck,
    removeCheck,
    clearHistory,
    clearRecents,
    markIntroSeen,
  } = useCostToolMemory(orgId);

  const years = usePlanYears(import.meta.env.VITE_API_BASE_URL ?? "");
  // The partner's own pick; the effective `planYear` is null while no toggle
  // renders, and then no request carries the flag. `shownYear` is the year the
  // chips and cards answer for, the current year while the toggle is away, and
  // `storedYear` is what the memory writes: next year, or nothing for the
  // current year, so stored rows and entries without a year keep reading as
  // the current year whatever year that is.
  const [pickedYear, setPickedYear] = useState<number | null>(null);
  const planYear = years === null || years.next === null ? null : (pickedYear ?? years.next);
  const currentYear = years?.current ?? new Date().getFullYear();
  const shownYear = planYear ?? currentYear;
  const storedYear = shownYear > currentYear ? shownYear : undefined;

  // Question 1's state code, lifted here (not in StateSection) because it
  // gates the plan search and the result screen's context row names it.
  const [usState, setUsState] = useState(memory.state);
  const [plan, setPlan] = useState<CostToolPlanOption | null>(null);
  const [drugs, setDrugs] = useState<Drug[]>([]);
  // Partner-edited per-fill quantities only, keyed by rxcui. Engine-assumed
  // quantities never appear here, so the map is exactly what the POST sends.
  const [quantities, setQuantities] = useState<Record<string, number>>({});
  const drugInputRef = useRef<HTMLInputElement | null>(null);
  // "Check another patient" on the result screen asks for focus in the drug
  // input, which exists only once the form screen renders again; the effect
  // below completes the move after the navigation.
  const [pendingDrugFocus, setPendingDrugFocus] = useState(false);
  const [estimate, setEstimate] = useState<CostToolEstimate | null>(null);
  // The input key (plan, drugs, quantities) the current `estimate` answered.
  // A mismatch with the live inputs means the shown numbers answer a different
  // question; see the `stale` prop on ResultScreen.
  const [estimateKey, setEstimateKey] = useState<string | null>(null);
  const [estimating, setEstimating] = useState(false);
  const [estimateError, setEstimateError] = useState(false);
  // Stale-response guard and manual-retry trigger.
  const seqRef = useRef(0);
  const [retryNonce, setRetryNonce] = useState(0);

  const rxcuis = drugs.map((d) => d.rxcui);
  const rxcuisKey = rxcuis.join(",");
  const planId = plan?.id ?? null;
  // Deterministic (sorted) key so a quantity edit re-fires the estimate
  // through the same debounce; the effect reads the object via a ref.
  const quantitiesKey = JSON.stringify(
    Object.entries(quantities).sort(([a], [b]) => a.localeCompare(b)),
  );
  // Everything the estimate POST depends on, as one comparable string. A
  // success stamps it into `estimateKey`.
  const inputKey = `${planId ?? ""}|${planYear ?? ""}|${rxcuisKey}|${quantitiesKey}`;

  // The estimate effect keys off the string keys only. The request body and
  // the history write need the full objects, so they read them through refs
  // kept current every render instead of widening the dependency set.
  const planRef = useRef(plan);
  planRef.current = plan;
  const drugsRef = useRef(drugs);
  drugsRef.current = drugs;
  const quantitiesRef = useRef(quantities);
  quantitiesRef.current = quantities;
  const storedYearRef = useRef(storedYear);
  storedYearRef.current = storedYear;

  useEffect(() => {
    const seq = ++seqRef.current;
    if (planId === null || rxcuisKey === "") {
      // Incomplete input: drop any pending run and clear the result.
      setEstimate(null);
      setEstimateKey(null);
      setEstimating(false);
      setEstimateError(false);
      return;
    }
    // The question this run answers, stamped onto the estimate on success so
    // ResultScreen can tell a current answer from a stale one.
    const key = `${planId}|${planYear ?? ""}|${rxcuisKey}|${quantitiesKey}`;
    setEstimating(true);
    setEstimateError(false);
    const timer = window.setTimeout(() => {
      const overrides = quantitiesRef.current;
      api<CostToolEstimate>(`/api/partner/orgs/${orgId}/cost-tool/estimate`, {
        method: "POST",
        body: {
          planId,
          rxcuis: rxcuisKey.split(","),
          ...(Object.keys(overrides).length > 0 ? { quantities: overrides } : {}),
          ...(planYear !== null ? { planYear } : {}),
        },
      })
        .then((res) => {
          if (seq !== seqRef.current) return; // A newer run superseded this one.
          setEstimate(res);
          setEstimateKey(key);
          setEstimating(false);
          // Record the settled check. The seq guard doubles as the write
          // debounce, because only the estimate that lands records, and
          // `rememberCheck` folds re-checks of the same plan and drugs into one
          // entry. Results with unknown coverage are not recorded.
          const settledPlan = planRef.current;
          const settledDrugs = drugsRef.current;
          if (res.coverageKnown && settledPlan !== null && settledDrugs.length > 0) {
            const settledYear = storedYearRef.current;
            rememberCheck({
              plan: settledPlan,
              ...(settledYear === undefined ? {} : { planYear: settledYear }),
              drugs: settledDrugs,
              biggestMonth: res.biggestMonth,
              biggestMonthMin: res.biggestMonthMin,
              monthlyWithM3p: res.monthlyWithM3p,
              monthlyWithM3pMin: res.monthlyWithM3pMin,
              verdict: res.unavailableReason !== null ? "unknown" : res.verdict,
            });
          }
        })
        .catch(() => {
          if (seq !== seqRef.current) return;
          setEstimating(false);
          setEstimateError(true);
        });
    }, ESTIMATE_DEBOUNCE_MS);
    return () => window.clearTimeout(timer);
  }, [api, orgId, planId, planYear, rxcuisKey, quantitiesKey, retryNonce, rememberCheck]);

  // Complete a cross-screen focus request once the drug input exists. The
  // router applies the navigation in a transition after the request flag
  // renders, so the input isn't mounted on the flag's own render; the
  // location dependency re-runs the effect when the form screen is.
  useEffect(() => {
    if (!pendingDrugFocus) return;
    const el = drugInputRef.current;
    if (el === null) return;
    el.scrollIntoView({ block: "center" });
    el.focus();
    setPendingDrugFocus(false);
  }, [pendingDrugFocus, location]);

  const estimateByRxcui = useMemo(() => {
    const map = new Map<string, CostToolEstimateDrug>();
    for (const row of estimate?.drugs ?? []) map.set(row.rxcui, row);
    return map;
  }, [estimate]);

  // The current estimate's inputs, for the referral handoff. Present only when
  // a settled estimate answers the live inputs (not stale) and a plan is set,
  // so the modal attaches the cost-report PDF for exactly what's on screen. It
  // carries the same planId + rxcuis (+ quantities, + planYear) the estimate
  // POST used, so the backend recomputes the identical numbers.
  const handoffContext =
    estimate !== null && estimateKey === inputKey && planId !== null
      ? {
          planId,
          rxcuis,
          ...(Object.keys(quantities).length > 0 ? { quantities } : {}),
          ...(planYear !== null ? { planYear } : {}),
        }
      : undefined;

  /** Question 1: pick a state. It narrows the plan search and is remembered. */
  const changeState = (code: string): void => {
    setUsState(code);
    rememberState(code);
  };

  /** Select a plan (search or recent chip): remember it, focus the drug input. */
  const selectPlan = (next: CostToolPlanOption | null): void => {
    setPlan(next);
    if (next === null) return;
    rememberPlan(next, storedYear);
    drugInputRef.current?.focus();
  };

  /**
   * The toggle's change: the other year's plan list may not hold the selected
   * plan, so it clears (and the result with it); the drugs stay. Only the
   * toggle clears, so a history card can set the year and the plan together.
   */
  const changePlanYear = (year: number): void => {
    setPickedYear(year);
    setPlan(null);
  };

  // The recent-plan chips for the effective year only, so a plan picked under
  // next year is not offered while this year is selected, and the other way
  // round. A row without a stored year reads as the current year.
  const recentPlans = memory.recentPlans.filter((p) => (p.planYear ?? currentYear) === shownYear);

  /** Add a drug (search or recent chip): remember it, keep focus in the input. */
  const addDrug = (drug: Drug): void => {
    setDrugs((prev) =>
      prev.length >= MAX_DRUGS || prev.some((d) => d.rxcui === drug.rxcui) ? prev : [...prev, drug],
    );
    rememberDrug(drug);
    drugInputRef.current?.focus();
  };
  const removeDrug = (rxcui: string): void => {
    setDrugs((prev) => prev.filter((d) => d.rxcui !== rxcui));
    // A removed drug's override must not carry into later requests or a re-add.
    setQuantities((prev) => omitKey(prev, rxcui));
  };

  /**
   * Swap a drug for another strength or form of itself, keeping its list
   * position. The old rxcui's quantity override is dropped, because a
   * different strength means a different fill quantity. The new variant is
   * remembered like any other selection.
   */
  const replaceDrug = (oldRxcui: string, newDrug: Drug): void => {
    setDrugs((prev) => prev.map((d) => (d.rxcui === oldRxcui ? newDrug : d)));
    setQuantities((prev) => omitKey(prev, oldRxcui));
    rememberDrug(newDrug);
  };

  /** Set a partner override (`value`) or return the row to assumed (`null`). */
  const changeQuantity = (rxcui: string, value: number | null): void => {
    setQuantities((prev) => (value === null ? omitKey(prev, rxcui) : { ...prev, [rxcui]: value }));
  };

  /**
   * Start over for the next patient at the counter ("Check another patient",
   * on the result screen).
   *
   * Clears the drugs, the quantity overrides and (through the estimate
   * effect) the result, but keeps the plan and state, because a pharmacy
   * counter mostly re-checks against the same local plans and the state is
   * remembered across visits anyway. The navigation returns to the form, and
   * the pending-focus effect puts the cursor in the drug input there, so the
   * next check starts with typing.
   */
  const checkAnotherPatient = (): void => {
    setDrugs([]);
    setQuantities({});
    setPendingDrugFocus(true);
    void navigate(formPath);
  };

  /**
   * Restore a recent check by setting its inputs into the form.
   *
   * The estimate effect re-runs against current data, and the seq guard lets
   * the restore win over a run in flight. The entry moves to the front only
   * when the re-run settles and `rememberCheck` records it, so a tap alone
   * never reorders. Focus moves to the drug input. The re-run uses the entry's
   * year; an entry without one, or from a year before the current one (a
   * check from last December), re-runs under the current year.
   */
  const restoreCheck = (entry: CostToolHistoryEntry): void => {
    setPickedYear(Math.max(entry.planYear ?? currentYear, currentYear));
    setPlan(entry.plan);
    setDrugs(entry.drugs);
    // History entries hold no quantity overrides, so the restore clears them.
    setQuantities({});
    drugInputRef.current?.focus();
  };

  // Question statuses, computed once so the three cards agree. A question is
  // done when it has an answer (a state, a plan, at least one medication), and
  // the first one not done is current. The status is guidance only; every
  // section stays interactive, so a pharmacist may add drugs before the plan.
  const stepDone = [usState !== "", plan !== null, drugs.length > 0];
  const currentStep = stepDone.indexOf(false);
  const stepStatus = (i: number): StepStatus =>
    stepDone[i] === true ? "done" : i === currentStep ? "current" : "upcoming";

  const canRun = plan !== null && drugs.length > 0;

  const formScreen = (
    <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_21rem] lg:items-start lg:gap-8">
      <div className="min-w-0">
        <header>
          <div className="flex flex-wrap items-center gap-x-2">
            <h2 className="text-foreground font-serif text-[34px] font-semibold leading-[42px]">
              Member cost share calculator
            </h2>
            <HelpWalkthrough
              seenIntro={memory.seenIntro === true}
              onSeen={markIntroSeen}
              hasNextYear={years !== null && years.next !== null}
            />
          </div>
          <p className="text-foreground mt-1 text-[17px] leading-6">
            Patient name or date of birth not required.
          </p>
        </header>

        <div className="mt-6 space-y-[18px]">
          <StateSection state={usState} onStateChange={changeState} status={stepStatus(0)} />

          <PlanSection
            orgId={orgId}
            plan={plan}
            onPlanChange={selectPlan}
            status={stepStatus(1)}
            state={usState}
            years={years}
            planYear={planYear}
            onPlanYearChange={changePlanYear}
            recentPlans={recentPlans}
            onClearRecents={clearRecents}
          />

          <MedicationsSection
            orgId={orgId}
            drugs={drugs}
            onAdd={addDrug}
            onRemove={removeDrug}
            onReplace={replaceDrug}
            status={stepStatus(2)}
            estimateByRxcui={estimateByRxcui}
            quantities={quantities}
            onQuantityChange={changeQuantity}
            refreshing={estimating && estimate !== null}
            recentDrugs={memory.recentDrugs}
            onClearRecents={clearRecents}
            inputRef={drugInputRef}
          />

          <button
            type="button"
            disabled={!canRun}
            onClick={() => void navigate(resultPath)}
            className={buttonClasses("secondary", "md", "w-full gap-2 text-[17px]")}
          >
            <img src={calculatorIcon} alt="" className="size-[18px] shrink-0" />
            See the member&rsquo;s cost share
          </button>

          <HandoffSection orgId={orgId} estimateContext={handoffContext} />
        </div>
      </div>

      {/* One HistoryPanel instance serves both breakpoints, because a copy per
          breakpoint would double every button in the accessibility tree. Below
          lg it shows its disclosure variant. */}
      <aside className="mt-8 space-y-[18px] lg:sticky lg:top-6 lg:mt-0">
        <InfoAside />
        <HistoryPanel
          entries={memory.history}
          currentYear={currentYear}
          onRestore={restoreCheck}
          onRemove={removeCheck}
          onClear={clearHistory}
        />
      </aside>
    </div>
  );

  return (
    <Routes>
      <Route index element={formScreen} />
      <Route
        path="result"
        element={
          // A result URL with nothing to show (no plan or no medications, as
          // after a full page load) sends the pharmacist to the questions.
          canRun && plan !== null ? (
            <ResultScreen
              orgId={orgId}
              plan={plan}
              stateCode={usState}
              drugCount={drugs.length}
              estimate={estimate}
              stale={estimateKey !== null && estimateKey !== inputKey}
              loading={estimating}
              error={estimateError}
              anyAssumed={(estimate?.drugs ?? []).some((d) => d.covered && d.quantityAssumed)}
              onRetry={() => setRetryNonce((n) => n + 1)}
              estimateContext={handoffContext}
              onCheckAnother={checkAnotherPatient}
              backTo={formPath}
            />
          ) : (
            <Navigate to={formPath} replace />
          )
        }
      />
      <Route path="*" element={<Navigate to={formPath} replace />} />
    </Routes>
  );
}

/**
 * Static info card on what the estimate means (Figma dR8cJ6hRSW5eERToo64boe,
 * node 2340:212). The Extra Help line is compliance copy: eligibility is
 * DearBill's call with the patient, never this screen's.
 */
function InfoAside(): ReactNode {
  return (
    <section
      aria-labelledby="cost-tool-info-heading"
      className="border-border-strong bg-ice text-deep-navy rounded-2xl border p-[22px]"
    >
      <h3
        id="cost-tool-info-heading"
        className="font-serif text-[19px] font-semibold leading-[26px]"
      >
        What the estimate shows
      </h3>
      <p className="mt-2.5 leading-6">
        If the patient&rsquo;s biggest pharmacy co-pay bill is larger than $100 and spreading the
        payment into smaller monthly amounts would help.
      </p>
      <h3 className="mt-2.5 font-serif text-[19px] font-semibold leading-[26px]">
        What it does not show
      </h3>
      <p className="mt-2.5 leading-6">
        If this patient qualifies for Extra Help. DearBill will check with patient to determine
        eligibility.
      </p>
    </section>
  );
}

/** A copy of `record` without `key`; the same reference when `key` is absent. */
function omitKey(record: Record<string, number>, key: string): Record<string, number> {
  if (!(key in record)) return record;
  const { [key]: _, ...rest } = record;
  return rest;
}
